import { useQuery } from '@tanstack/react-query'
import { controlCenterApi } from '@/lib/api'
import PageHeader from '@/components/PageHeader'

type PortMap = { containerPort: string; hostPort?: string; container: string }
type Network = { name: string; containers: string[]; ports: PortMap[] }
type NetworksResponse = { success?: boolean; networks?: Network[] }

export default function Networks() {
  const { data, isLoading, isError } = useQuery<NetworksResponse>({
    queryKey: ['control-center', 'networks'],
    queryFn: controlCenterApi.listNetworks,
  })

  const networks = data?.networks ?? []

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="Networks"
        description={`${networks.length} Docker networks active.`}
      />

      {isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Failed to load network topology.
        </div>
      ) : null}

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : networks.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-card/50 px-6 py-12 text-center text-sm text-muted-foreground">
          No networks visible.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {networks.map((net) => (
            <div key={net.name} className="rounded-lg border border-border bg-card p-5 space-y-3">
              <div className="flex items-baseline justify-between">
                <h3 className="font-semibold">{net.name}</h3>
                <span className="text-xs text-muted-foreground">
                  {net.containers.length} container{net.containers.length === 1 ? '' : 's'}
                </span>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">Containers</div>
                <ul className="text-xs space-y-0.5 font-mono">
                  {net.containers.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              </div>

              {net.ports.length > 0 ? (
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">Ports</div>
                  <ul className="text-xs space-y-0.5 font-mono">
                    {net.ports.map((p, i) => (
                      <li key={`${p.container}-${p.containerPort}-${i}`} className="text-muted-foreground">
                        <span className="text-foreground">
                          {p.hostPort ? `${p.hostPort}→` : ''}
                          {p.containerPort}
                        </span>{' '}
                        ({p.container})
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
